import { useEffect } from "react";
import { MdOutlineClose } from "react-icons/md";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import useKeyboardViewport from "@/hooks/global/useKeyboardViewport";

const DEFAULT_PANEL_CLASS = "max-w-[648px] rounded-lg";

const Modal = ({
  isOpen,
  onClose,
  title,
  header,
  panelClassName = DEFAULT_PANEL_CLASS,
  dismissible = false,
  children,
}) => {
  // both have to run before the early return to keep the hook order stable
  // the dialog is fixed-positioned, so collapsing the scrollport cannot strand
  // it the way it would a sticky ancestor
  useBodyScrollLock(isOpen, { lockViewport: true });
  const keyboardViewport = useKeyboardViewport(isOpen);

  // opt-in: a stray backdrop click or Esc must not discard a half-filled form
  useEffect(() => {
    if (!isOpen || !dismissible) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, dismissible, onClose]);

  if (!isOpen) return null;

  return (
    <div
      // once the keyboard is up, only the visual viewport says what is still
      // visible; without one the CSS sizing already does, and overriding it on
      // every URL-bar change re-centres the dialog
      style={
        keyboardViewport
          ? {
              top: keyboardViewport.offsetTop,
              height: keyboardViewport.height,
              bottom: "auto",
            }
          : undefined
      }
      className="fixed inset-0 flex items-center justify-center p-3 sm:p-4 z-[9998]"
    >
      {/* follows browser chrome, unlike the box that centres the dialog */}
      <div
        aria-hidden="true"
        className="fixed inset-0 h-[100dvh] bg-lightblack opacity-50"
        onClick={dismissible ? onClose : undefined}
      />
      <div
        className={`relative z-10 flex flex-col w-full max-h-[90%] overflow-hidden bg-white shadow-md ${panelClassName}`}
      >
        {header || (
          <div className="flex items-center justify-between gap-4 px-4 pt-2 sm:px-6 shrink-0">
            <h2 className="my-4 text-xl">{title || ""}</h2>
            <MdOutlineClose
              className="text-3xl transition-colors duration-500 cursor-pointer text-slate-400 hover:text-slate-500"
              onClick={onClose}
            />
          </div>
        )}
        <div className="px-4 pb-5 overflow-y-auto overscroll-contain sm:px-6 sm:pb-6 red-scrollbar">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
